"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { ThemeContext, type ThemeContextValue } from "../hooks/use-theme";

const MEDIA = "(prefers-color-scheme: dark)";
const STORAGE_KEY = "theme";

interface ThemeProviderProps {
	children: React.ReactNode;
	defaultTheme?: string;
	attribute?: string;
	disableTransitionOnChange?: boolean;
	enableColorScheme?: boolean;
}

export function ThemeProvider({
	children,
	defaultTheme = "dark",
	attribute = "class",
	disableTransitionOnChange = false,
	enableColorScheme = false,
}: ThemeProviderProps) {
	const [theme, setThemeState] = useState(() => {
		if (typeof window === "undefined") return defaultTheme;
		try {
			return localStorage.getItem(STORAGE_KEY) || defaultTheme;
		} catch {
			return defaultTheme;
		}
	});

	const [systemTheme, setSystemTheme] = useState<string>(() => {
		if (typeof window === "undefined") return "dark";
		return window.matchMedia(MEDIA).matches ? "dark" : "light";
	});

	const resolvedTheme = theme === "system" ? systemTheme : theme;

	const applyTheme = useCallback(
		(resolved: string) => {
			const d = document.documentElement;
			if (attribute === "class") {
				d.classList.remove("light", "dark");
				d.classList.add(resolved);
			} else {
				d.setAttribute(attribute, resolved);
			}
			if (enableColorScheme) {
				d.style.colorScheme = resolved;
			}
			if (disableTransitionOnChange) {
				const style = document.createElement("style");
				style.appendChild(
					document.createTextNode(
						"*,*::before,*::after{-webkit-transition:none!important;-moz-transition:none!important;-o-transition:none!important;-ms-transition:none!important;transition:none!important}",
					),
				);
				document.head.appendChild(style);
				window.getComputedStyle(document.body);
				setTimeout(() => document.head.removeChild(style), 1);
			}
		},
		[attribute, enableColorScheme, disableTransitionOnChange],
	);

	const setTheme = useCallback((value: string) => {
		setThemeState(value);
		try {
			localStorage.setItem(STORAGE_KEY, value);
		} catch {}
	}, []);

	// Listen for system theme changes
	useEffect(() => {
		const media = window.matchMedia(MEDIA);
		const handler = (e: MediaQueryListEvent) =>
			setSystemTheme(e.matches ? "dark" : "light");
		media.addEventListener("change", handler);
		return () => media.removeEventListener("change", handler);
	}, []);

	// Listen for storage changes (cross-tab sync)
	useEffect(() => {
		const handler = (e: StorageEvent) => {
			if (e.key === STORAGE_KEY) {
				setThemeState(e.newValue || defaultTheme);
			}
		};
		window.addEventListener("storage", handler);
		return () => window.removeEventListener("storage", handler);
	}, [defaultTheme]);

	// Apply theme on changes
	useEffect(() => {
		applyTheme(resolvedTheme);
	}, [resolvedTheme, applyTheme]);

	const value = useMemo<ThemeContextValue>(
		() => ({
			theme,
			setTheme,
			resolvedTheme,
			themes: ["light", "dark", "system"],
			systemTheme,
		}),
		[theme, setTheme, resolvedTheme, systemTheme],
	);

	return <ThemeContext value={value}>{children}</ThemeContext>;
}
